/**
 * System Values
 *
 * Provides runtime-computed values available in `{expression}` bindings.
 * These are read-only, system-provided values that sit at the bottom of the
 * expression scope chain — user-declared properties with the same name
 * take precedence.
 *
 * ## Available values
 *
 * | Expression        | Type   | Description                              |
 * |-------------------|--------|------------------------------------------|
 * | time.elapsed      | number | Seconds since play mode started          |
 * | camera.distance   | number | Meters from this object to camera        |
 * | camera.position   | Vec3   | Camera world position                    |
 * | self.position     | Vec3   | This object's world position             |
 *
 * ## Design
 *
 * All values are computed lazily when `buildScope()` is called — no per-frame
 * update loop needed. Time reads the scene's clock (performance.now by default)
 * minus play start time. Headless hosts can inject an explicit clock.
 * Camera/self read live Babylon.js transform state.
 *
 * System values are injected into the expression scope by `_evaluateExpr()`.
 * They use dotted notation (e.g. `camera.distance`) so they're only available
 * inside `{expression}` bindings, not as simple `$name` refs.
 *
 * See docs/canopy/CANOPY_EXPRESSIONS.md for the expression system spec.
 */

import type { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';

/**
 * Provides system-computed values for expression evaluation.
 * One instance per TraceScene.
 */
export class SystemValueProvider {
    /** Timestamp (ms) when play mode started; null when not playing. */
    private _playStartTime: number | null = null;
    private _now: () => number = () => performance.now();

    /** Camera ref — set by TraceScene */
    private _camera: ArcRotateCamera | null = null;

    // ── Lifecycle ──

    /** Inject a scene-local clock for deterministic hosts, preserving elapsed time. */
    setClock(now: () => number): void {
        const elapsedMs = this.getElapsed() * 1000;
        this._now = now;
        if (this._playStartTime !== null) this._playStartTime = now() - elapsedMs;
    }

    /** Called when play mode changes */
    setPlayMode(playing: boolean): void {
        if (playing) {
            this._playStartTime = this._now();
        } else {
            this._playStartTime = null;
        }
    }

    /** Set the active camera reference. Only ArcRotateCamera is supported — other camera types will produce zero distance/position. */
    setCamera(camera: ArcRotateCamera | null): void {
        this._camera = camera;
    }

    // ── Scope building ──

    /**
     * Build the system values scope for expression evaluation.
     * All values are computed on demand from live Babylon.js state.
     *
     * @param transform - The owning SceneObject's transform node
     *                    (needed for per-object values like camera.distance)
     */
    buildScope(transform: TransformNode): Record<string, unknown> {
        return {
            time: { elapsed: this.getElapsed() },
            camera: this._buildCamera(transform),
            self: this._buildSelf(transform),
        };
    }

    // ── Value builders ──

    /** Seconds since play mode started. Returns 0 when not playing. */
    getElapsed(): number {
        return this._playStartTime !== null
            ? (this._now() - this._playStartTime) / 1000
            : 0;
    }

    private _buildCamera(transform: TransformNode): { distance: number; position: { x: number; y: number; z: number } } {
        if (!this._camera) {
            return { distance: 0, position: { x: 0, y: 0, z: 0 } };
        }
        const camPos = this._camera.position;
        const objPos = transform.getAbsolutePosition();
        const dx = camPos.x - objPos.x;
        const dy = camPos.y - objPos.y;
        const dz = camPos.z - objPos.z;
        return {
            distance: Math.sqrt(dx * dx + dy * dy + dz * dz),
            position: { x: camPos.x, y: camPos.y, z: camPos.z },
        };
    }

    private _buildSelf(transform: TransformNode): { position: { x: number; y: number; z: number } } {
        const pos = transform.getAbsolutePosition();
        return {
            position: { x: pos.x, y: pos.y, z: pos.z },
        };
    }
}
